import browserConsoleTransporter from './browser-console-transporter';
import * as Constants from './constants';
import { Levels, LogArg, Transporter } from './types';

type Options = {
  transporters?: Transporter[];
};

export class ScopedLoggerManager {
  private readonly mfeId: string;
  private readonly transporters: Transporter[];

  constructor(mfeId: string, options?: Options) {
    const { transporters = [] } = options ?? {};

    this.mfeId = mfeId;
    this.transporters = [browserConsoleTransporter(), ...transporters];
  }

  addTransporter(transporter: Transporter) {
    this.transporters.push(transporter);
  }

  private send(
    scope: string,
    level: Levels,
    message?: LogArg,
    ...optionalParams: LogArg[]
  ) {
    const timestamp = new Date().getTime();
    let namespace = this.mfeId;
    if (scope) {
      namespace = `${this.mfeId}:${scope}`;
    }

    this.transporters.forEach((transporter) => {
      try {
        transporter[level]?.({ timestamp, namespace, message, optionalParams });
      } catch (e) {
        // no-op
      }
    });
  }

  debug(message?: LogArg, ...optionalParams: LogArg[]) {
    this.send('', Constants.LOG_LEVEL_DEBUG, message, ...optionalParams);
  }

  info(message?: LogArg, ...optionalParams: LogArg[]) {
    this.send('', Constants.LOG_LEVEL_INFO, message, ...optionalParams);
  }

  warn(message?: LogArg, ...optionalParams: LogArg[]) {
    this.send('', Constants.LOG_LEVEL_WARN, message, ...optionalParams);
  }

  error(message?: LogArg, ...optionalParams: LogArg[]) {
    this.send('', Constants.LOG_LEVEL_ERROR, message, ...optionalParams);
  }

  /**
   * Return a logger for a specific scope
   */
  createScopedLogger(scope: string) {
    return {
      debug: (message?: LogArg, ...optionalParams: LogArg[]) =>
        this.send(scope, Constants.LOG_LEVEL_DEBUG, message, ...optionalParams),
      info: (message?: LogArg, ...optionalParams: LogArg[]) =>
        this.send(scope, Constants.LOG_LEVEL_INFO, message, ...optionalParams),
      warn: (message?: LogArg, ...optionalParams: LogArg[]) =>
        this.send(scope, Constants.LOG_LEVEL_WARN, message, ...optionalParams),
      error: (message?: LogArg, ...optionalParams: LogArg[]) =>
        this.send(scope, Constants.LOG_LEVEL_ERROR, message, ...optionalParams),
    };
  }
}
